import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, X } from "lucide-react";

import { cn, stripDiacritics } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { countries } from "@/constants/countries";

interface CountryComboboxProps {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  wrapperClassName?: string;
  rightElement?: React.ReactNode;
  tooltip?: string;
  isDisabled?: boolean;
  error?: string;
  onChange?: () => void;
  allowedCodes?: string[];
}

// Component for country flag with error handling
const CountryFlag = ({ countryCode }: { countryCode: string }) => {
  const { t } = useTranslation("shared");
  const [imageError, setImageError] = React.useState(false);

  if (imageError) {
    return (
      <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-gray-200">
        <span className="text-xs text-[#94A3B8]">{countryCode}</span>
      </div>
    );
  }

  return (
    <img
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode.toUpperCase()}.svg`}
      alt={t("countryCombobox.flagAlt", { countryCode })}
      className="h-4 w-4 rounded-sm object-cover"
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
};

export const CountryCombobox = ({
  value,
  onValueChange,
  defaultValue,
  label,
  placeholder,
  className,
  clearable = false,
  wrapperClassName,
  rightElement,
  tooltip,
  isDisabled = false,
  error,
  onChange,
  allowedCodes,
}: CountryComboboxProps) => {
  const { t } = useTranslation("shared");
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const commandGroupRef = React.useRef<HTMLDivElement>(null);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
    onChange?.();
  };

  const selectedCountry = countries.find((country) => country.countryCode === currentValue);

  const hasError = !!error;

  // Scroll to top when input changes
  React.useEffect(() => {
    if (!open) return;

    let cleanup: (() => void) | null = null;
    let retryCount = 0;
    const maxRetries = 10;

    // Find the input element inside the Command component
    const findInput = (): HTMLInputElement | null => {
      if (!popoverContentRef.current) return null;
      // Try multiple selectors to find the input
      return (
        popoverContentRef.current.querySelector<HTMLInputElement>("input[cmdk-input]") ||
        popoverContentRef.current.querySelector<HTMLInputElement>(
          "div[cmdk-input-wrapper] input",
        ) ||
        popoverContentRef.current.querySelector<HTMLInputElement>("input")
      );
    };

    const handleInput = () => {
      if (commandGroupRef.current) {
        commandGroupRef.current.scrollTop = 0;
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const setupListener = () => {
      const input = findInput();
      if (!input) {
        retryCount++;
        if (retryCount < maxRetries) {
          // Retry if not found yet (portal might not be ready)
          requestAnimationFrame(setupListener);
        } else {
          console.warn("Could not find input element after retries");
        }
        return;
      }

      input.addEventListener("input", handleInput);

      cleanup = () => {
        input.removeEventListener("input", handleInput);
      };
    };

    requestAnimationFrame(setupListener);

    // Cleanup function for the effect
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [open]);

  return (
    <div className={cn("flex flex-col gap-2", wrapperClassName)}>
      <div className="relative">
        <div
          className={cn(
            "relative flex h-[2.43rem] w-full items-center rounded-lg border bg-white px-4 transition-all duration-200",
            {
              "border-blue-600": open,
              "border-red-500": hasError,
              "border-gray-300": !open && !hasError,
              "bg-gray-50": isDisabled,
              "pointer-events-none": isDisabled,
              "hover:border-gray-400": !isDisabled && !open && !hasError,
            },
            className,
          )}
        >
          {label && (
            <Label
              className={cn(
                "pointer-events-none absolute text-sm text-[#94A3B8] transition-all duration-200 ease-in-out",
                {
                  "-translate-y-3 text-xs": currentValue && currentValue.toString().length > 0,
                  "translate-y-0 text-sm": !currentValue || currentValue.toString().length === 0,
                },
              )}
            >
              {label}
            </Label>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  "flex h-[100%] w-full justify-between border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus:ring-0 focus:outline-none",
                  {
                    "translate-y-1.5": label && currentValue && currentValue.toString().length > 0,
                  },
                )}
                disabled={isDisabled}
              >
                <div className="flex items-center gap-2">
                  {selectedCountry ? (
                    <>
                      <CountryFlag countryCode={selectedCountry.countryCode} />
                      <span className="truncate">{selectedCountry.label_fr}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground font-normal">
                      {placeholder ?? t("countryCombobox.placeholder")}
                    </span>
                  )}
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent ref={popoverContentRef} className="w-[400px] p-0">
              <Command>
                <CommandInput
                  placeholder={t("countryCombobox.searchPlaceholder")}
                  className="h-9"
                />
                <CommandList>
                  <CommandEmpty>{t("countryCombobox.empty")}</CommandEmpty>
                  <CommandGroup ref={commandGroupRef} className="max-h-[300px] overflow-y-auto">
                    {(allowedCodes
                      ? countries.filter((c) => allowedCodes.includes(c.countryCode))
                      : countries
                    ).map((country) => (
                      <CommandItem
                        key={country.countryCode}
                        value={`${country.countryCode} ${country.label_fr} ${stripDiacritics(
                          country.label_fr,
                        )}`}
                        onSelect={() => {
                          handleValueChange(country.countryCode);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <CountryFlag countryCode={country.countryCode} />
                          <span>{country.label_fr}</span>
                        </div>
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            currentValue === country.countryCode ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {rightElement && <div className="ml-2 flex flex-row items-center">{rightElement}</div>}

          {tooltip && (
            <div className="has-tooltip ml-2 self-center">
              <span className="tooltip bottom-3/4 w-max max-w-64 -translate-x-1/4 rounded-lg bg-blue-500 p-2 text-center text-sm font-semibold text-white shadow-lg">
                {tooltip}
              </span>
              <svg
                className="size-6 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          )}

          {clearable && currentValue && !isDisabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-1/2 right-8 h-6 w-6 -translate-y-1/2 p-0 hover:bg-transparent"
              onClick={() => handleValueChange("")}
            >
              <X className="h-3 w-3" />
              <span className="sr-only">{t("countryCombobox.clear")}</span>
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
